import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { StoreService } from './store.service';
import {
  catchError,
  filter,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class StoreInfoService {
  private storeListSubject = new BehaviorSubject<any>(null);
  public storeList$ = this.storeListSubject.asObservable();
  storeList;
  private storeInfoSubject = new BehaviorSubject<any>(null);
  public storeInfo$ = this.storeInfoSubject.asObservable();
  storeInfo;
  private generateAiContentStatusSubject = new BehaviorSubject<any>(null);
  public generateAiContentStatus$ =
    this.generateAiContentStatusSubject.asObservable();

  private generateContentQueuePollingSub: Subscription | null = null;

  constructor(private svc: StoreService, private router: Router) {
    // this.fetchStoreList();
    // this.fetchStoreInfo();
  }

  fetchStoreList() {
    const token = localStorage?.getItem('bearer');
    if (token) {
      this.svc.getStoreLists(token).subscribe(
        (res) => {
          this.storeList = res['data'];
          // this.storeListSubject.next(this.storeList);
          console.log('check data whene loaded', this.storeList);
          this.updateStoreList(this.storeList);
        },
        (error) => {
          console.log(error);
        },
      );
    }
  }

  getStoreLists() {
    if (this.storeListSubject.value === null) {
      this.fetchStoreList();
    }
    return this.storeList$;
  }

  getStoreList(): Observable<any | null> {
    if (this.storeList === null) {
      this.fetchStoreList();
    }

    return this.storeList$.pipe(
      filter((data) => data !== null),
      take(1),
    );
  }

  updateStoreList(newStoreList: Partial<any>): void {
    this.storeListSubject.next(newStoreList);
    console.log('Set new storeList info:', newStoreList);
  }

  fetchStoreInfo() {
    const token = localStorage.getItem('bearer');
    const storeId = localStorage.getItem('store_id');
    if (token) {
      this.svc.getStoreInfo(token, storeId).subscribe(
        (res) => {
          this.storeInfo = res['data'];
          const generateAiContentStatusSummary =
            this.computeGenerateAiContentStatusSummary(this.storeInfo);
          const aiGeneratedStore = this.isAiGeneratedStore(this.storeInfo);

          this.storeInfo = {
            ...this.storeInfo,
            generate_ai_content_status_summary: generateAiContentStatusSummary,
            ai_generated_store: aiGeneratedStore,
          };
          this.storeInfoSubject.next(this.storeInfo);
          this.generateAiContentStatusSubject.next(generateAiContentStatusSummary);

          console.log('generateAiContentStatusSummary', generateAiContentStatusSummary);
          console.log('aiGeneratedStore', aiGeneratedStore);
          console.log(
            'this.isGenerateAiContentInProgress(generateAiContentStatusSummary)',
            this.isGenerateAiContentInProgress(generateAiContentStatusSummary),
          );
          if (
            aiGeneratedStore &&
            this.isGenerateAiContentInProgress(generateAiContentStatusSummary)
          ) {
            const existingMessageState = localStorage.getItem(
              'generated_ai_content_message',
            );
            if (existingMessageState === null) {
              localStorage.setItem('generated_ai_content_message', 'false');
            }
            this.startGenerateContentQueuePolling();
          } else {
            this.stopGenerateContentQueuePolling();
          }
          // this.updateStoreList(this.storeList);
        },
        (error) => {
          console.log(error);
          const status = error?.status;
          const message = error?.error?.error ?? error?.error?.message ?? error?.message;
          if (status === 401 || /unauthorized\s*user/i.test(message ?? '')) {
            this.router.navigate(['/login']);
          }
        },
      );
    }
  }

  getStoreInfo() {
    if (this.storeInfoSubject.value === null) {
      this.fetchStoreInfo();
    }
    return this.storeInfo$;
  }

  getStoreInfos(): Observable<any | null> {
    if (this.storeInfo === null) {
      this.fetchStoreInfo();
    }

    return this.storeInfo$.pipe(
      filter((data) => data !== null),
      take(1),
    );
  }

  getGenerateAiContentStatus(): Observable<any | null> {
    if (this.generateAiContentStatusSubject.value === null) {
      this.fetchStoreInfo();
    }

    return this.generateAiContentStatus$.pipe(
      filter((data) => data !== null),
      take(1),
    );
  }

  updateStoreInfo(newStoreInfo: Partial<any>): void {
    const currentStoreInfo = this.storeListSubject.value;
    if (currentStoreInfo) {
      const updatedStoreInfo = { ...currentStoreInfo, ...newStoreInfo };
      this.storeInfoSubject.next(updatedStoreInfo);
      console.log('Updated store info:', this.storeListSubject.value);
    } else {
      this.storeInfoSubject.next(newStoreInfo);
      console.log('Set new store info:', newStoreInfo);
    }
  }

  private computeGenerateAiContentStatusSummary(storeInfo: any): any {
    const list = storeInfo?.generate_ai_content_status;
    if (!Array.isArray(list) || list.length === 0) {
      return {
        status: 'NONE',
        by_topic: {},
        counts: {},
        total: 0,
      };
    }

    const byTopic = {};
    const counts = {};
    list.forEach((item) => {
      const topic = item?.content_topic;
      const status = item?.status;
      if (topic) {
        byTopic[topic] = status;
      }
      if (status) {
        counts[status] = (counts[status] ?? 0) + 1;
      }
    });

    const overallStatus =
      counts['FAILED'] > 0
        ? 'FAILED'
        : counts['PROCESSING'] > 0
          ? 'PROCESSING'
          : counts['QUEUE'] > 0
            ? 'QUEUE'
            : counts['DONE'] > 0
              ? 'DONE'
              : 'UNKNOWN';

    return {
      status: overallStatus,
      by_topic: byTopic,
      counts,
      total: list.length,
    };
  }

  private isAiGeneratedStore(storeInfo: any): boolean {
    return (
      Array.isArray(storeInfo?.generate_ai_content_status) &&
      storeInfo.generate_ai_content_status.length > 0
    );
  }

  private isGenerateAiContentInProgress(generateAiContentStatus: any): boolean {
    const status = generateAiContentStatus?.status;
    return status === 'QUEUE' || status === 'PROCESSING';
  }

  private startGenerateContentQueuePolling(): void {
    if (this.generateContentQueuePollingSub) {
      return;
    }

    const token = localStorage.getItem('bearer');
    const storeId = localStorage.getItem('store_id');
    if (!token || !storeId) {
      return;
    }

    this.generateContentQueuePollingSub = interval(10000)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.svc.getGenerateContentQueueStatus(token, storeId).pipe(
            tap((resp) => {
              console.log('[Generate AI Content Queue Status]', resp);
            }),
            catchError((err) => {
              console.log('[Generate AI Content Queue Status] error', err);
              return of(null);
            }),
          ),
        ),
      )
      .subscribe((resp: any) => {
        if (!resp) {
          return;
        }

        const latest = resp?.data ?? resp;
        if (!latest) {
          return;
        }

        const statusList = Array.isArray(latest)
          ? latest
          : latest?.generate_ai_content_status;

        if (Array.isArray(statusList)) {
          this.storeInfo = {
            ...(this.storeInfo ?? {}),
            generate_ai_content_status: statusList,
          };
          const generateAiContentStatusSummary =
            this.computeGenerateAiContentStatusSummary(this.storeInfo);
          const aiGeneratedStore = this.isAiGeneratedStore(this.storeInfo);

          this.storeInfo = {
            ...this.storeInfo,
            generate_ai_content_status_summary: generateAiContentStatusSummary,
            ai_generated_store: aiGeneratedStore,
          };

          this.storeInfoSubject.next(this.storeInfo);
          this.generateAiContentStatusSubject.next(generateAiContentStatusSummary);

          if (!this.isGenerateAiContentInProgress(generateAiContentStatusSummary)) {
            this.stopGenerateContentQueuePolling();
          }
        }
      });
  }

  private stopGenerateContentQueuePolling(): void {
    if (this.generateContentQueuePollingSub) {
      this.generateContentQueuePollingSub.unsubscribe();
      this.generateContentQueuePollingSub = null;
    }
  }
}
